import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { useState } from "react";
import { Menu, X } from "lucide-react";

const NAV = [
  { to: "/feed", label: "Stories" },
  { to: "/write", label: "Share" },
  { to: "/future-self", label: "Future Self" },
  { to: "/gratitude", label: "Gratitude" },
  { to: "/empathy", label: "Empathy" },
  { to: "/directory", label: "Counselors" },
  { to: "/founder", label: "Founder" },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  return (
    <div className="min-h-screen flex flex-col bg-mindora-bg">
      <header className="sticky top-0 z-40 glass border-b border-mindora-line">
        <div className="max-w-7xl mx-auto flex items-center justify-between px-6 lg:px-10 h-16">
          <Link to="/" className="flex items-baseline gap-1" data-testid="nav-home-link">
            <span className="font-serif text-2xl tracking-tight text-mindora-ink">mindora</span>
            <span className="w-1.5 h-1.5 rounded-full bg-mindora-red"></span>
          </Link>

          <nav className="hidden lg:flex items-center gap-7">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={`nav-link-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `text-sm tracking-wide transition-colors ${
                    isActive ? "text-mindora-red" : "text-mindora-ink/80 hover:text-mindora-red"
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            {user ? (
              <>
                {user.role === "admin" && (
                  <Link
                    to="/admin"
                    data-testid="nav-admin-link"
                    className="text-sm text-mindora-red hover:opacity-80 tracking-wide"
                  >
                    Admin
                  </Link>
                )}
                {user.role !== "admin" && (
                  <Link
                    to="/messages"
                    data-testid="nav-messages-link"
                    className="text-sm text-mindora-ink/80 hover:text-mindora-red"
                  >
                    Messages
                  </Link>
                )}
                <Link to={user.role === "therapist" ? "/therapist" : user.role === "admin" ? "/admin" : "/dashboard"} data-testid="nav-dashboard-link" className="text-sm text-mindora-ink/80 hover:text-mindora-red">
                  {user.name?.split(" ")[0] || "You"}
                </Link>
                <Button
                  variant="outline"
                  data-testid="nav-logout-button"
                  className="rounded-full border-mindora-line text-mindora-ink hover:bg-mindora-panel"
                  onClick={async () => { await logout(); navigate("/"); }}
                >
                  Sign out
                </Button>
              </>
            ) : (
              <>
                <Link to="/login" data-testid="nav-login-link" className="text-sm text-mindora-ink/80 hover:text-mindora-red">
                  Sign in
                </Link>
                <Button
                  data-testid="nav-join-button"
                  className="rounded-full btn-primary"
                  onClick={() => navigate("/register")}
                >
                  Begin
                </Button>
              </>
            )}
          </div>

          <button
            data-testid="nav-mobile-toggle"
            className="lg:hidden text-mindora-ink"
            onClick={() => setOpen(!open)}
            aria-label="Toggle menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>

        {open && (
          <div className="lg:hidden border-t border-mindora-line bg-mindora-bg" data-testid="nav-mobile-panel">
            <div className="max-w-7xl mx-auto px-6 py-4 flex flex-col gap-3">
              {NAV.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  onClick={() => setOpen(false)}
                  className={({ isActive }) =>
                    `text-base ${isActive ? "text-mindora-red" : "text-mindora-ink"}`
                  }
                >
                  {n.label}
                </NavLink>
              ))}
              <div className="h-px bg-mindora-line my-2" />
              {user ? (
                <>
                  <Link to="/dashboard" onClick={() => setOpen(false)} className="text-base text-mindora-ink">Dashboard</Link>
                  <button
                    className="text-left text-base text-mindora-red"
                    onClick={async () => { await logout(); setOpen(false); navigate("/"); }}
                  >Sign out</button>
                </>
              ) : (
                <>
                  <Link to="/login" onClick={() => setOpen(false)} className="text-base text-mindora-ink">Sign in</Link>
                  <Link to="/register" onClick={() => setOpen(false)} className="text-base text-mindora-red">Begin</Link>
                </>
              )}
            </div>
          </div>
        )}
      </header>

      <main className="flex-1">{children}</main>

      <footer className="border-t border-mindora-line mt-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12 grid grid-cols-1 md:grid-cols-3 gap-8 text-sm text-mindora-muted">
          <div>
            <div className="flex items-baseline gap-1">
              <span className="font-serif text-2xl text-mindora-ink">mindora</span>
              <span className="w-1.5 h-1.5 rounded-full bg-mindora-red"></span>
            </div>
            <p className="mt-3 max-w-xs leading-relaxed">
              A quiet corner of the internet for honest stories, small gratitudes, and the realization
              that you've never been the only one.
            </p>
          </div>
          <div>
            <div className="uppercase tracking-[0.2em] text-xs text-mindora-ink/70">Explore</div>
            <ul className="mt-3 space-y-2">
              <li><Link to="/feed" className="hover:text-mindora-red">Stories</Link></li>
              <li><Link to="/empathy" className="hover:text-mindora-red">Empathy Toolkit</Link></li>
              <li><Link to="/directory" className="hover:text-mindora-red">Counselors</Link></li>
              <li><Link to="/directory/apply" className="hover:text-mindora-red">Apply as a counselor</Link></li>
              <li><Link to="/counselor/login" className="hover:text-mindora-red">Counselor sign-in</Link></li>
              <li><Link to="/therapist" className="hover:text-mindora-red">Therapist portal</Link></li>
              <li><Link to="/founder" className="hover:text-mindora-red">Meet the founder</Link></li>
            </ul>
          </div>
          <div>
            <div className="uppercase tracking-[0.2em] text-xs text-mindora-ink/70">If you need help now</div>
            <p className="mt-3 leading-relaxed">
              If you are in crisis, please contact a local helpline immediately. Mindora is not a replacement
              for emergency mental health services.
            </p>
          </div>
        </div>
        <div className="text-center text-xs text-mindora-muted/80 pb-8">© {new Date().getFullYear()} Mindora · made with care</div>
      </footer>
    </div>
  );
}
